import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent, type MouseEvent, type PointerEvent, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { MemoryRouter, Routes, UNSAFE_LocationContext, UNSAFE_RouteContext, useLocation, useNavigate, type NavigateFunction } from 'react-router';
import { useTranslation } from 'react-i18next';
import { ArrowLeftRight, Columns2, X } from 'lucide-react';
import { Abas, idAba, idPainel } from '../componentes/abas';
import { Botao } from '../componentes/base';
import { ProvedorAcaoPrimaria } from '../componentes/acao-primaria';
import { LimiteErro } from '../componentes/limite-erro';
import { haCamadaAberta } from '../componentes/sobreposicao';
import { definirCaminhoDoLado } from '../nucleo/foco';
import { useCelular } from '../paginas/Externo';

/**
 * Lote 19 (item 13, pedido do Gustavo de 19/10): tela dividida, "da mesma forma que dá para dividir no Windows e no
 * navegador". A área de conteúdo vira dois painéis, cada um com a sua rota: o principal segue a URL da janela; o painel ao
 * lado tem um roteador próprio (memória), com a rota guardada na URL da janela no parâmetro `lado` (recarregar e compartilhar
 * voltam com os dois). Os dois usam a mesma sessão e o mesmo React Query (salvar num lado atualiza o outro); o aviso de nova
 * versão, o tempo real e os vigias continuam um só (ficam fora dos painéis).
 *
 *  - Entrar: "Abrir ao lado" no topo da página e no menu de contexto dos itens do menu lateral, ou Ctrl+Alt+seta para a
 *    direita; Ctrl+Alt+seta para a esquerda volta ao painel 1. "Trocar lados" e "Fechar o painel N" (o painel ativo).
 *  - O menu lateral navega no painel que tem o foco (marcado pela borda de cima no token de foco).
 *  - Divisória arrastável (mouse, toque e teclado), proporção por pessoa no aparelho, 360 px no mínimo por painel, duplo
 *    clique volta a 50/50.
 *  - Abaixo de 1024 px, os painéis viram as abas "Painel 1 | Painel 2", com os dois montados (nenhum perde a rota nem o que
 *    estava sendo digitado); no celular, a ação diz "Abrir em outra aba do painel".
 *  - Modal e Drawer do núcleo ocupam a janela (o foco preso é da janela inteira): ficam acima dos dois, e o painel de onde
 *    saíram segue marcado como ativo (o foco dentro da camada conta para o painel de origem, pela árvore do React).
 *
 * Lista de 30/10 B (ficha `2026-10-30_malgo_FUN_principal-clone-e-tela-dividida.md`, item 2): na casca do novo desenho, cada
 * painel tem o seu sub-sistema. O do painel 1 é o da aba (como sempre); o do painel 2 fica no estado e na URL (`lado_sub`), ao
 * lado do `lado`. O menu lateral e o seletor de sub-sistema seguem o painel ativo; trocar num painel não muda o outro. "Abrir ao
 * lado", "Trocar lados" e "Fechar o painel 1" levam o sub-sistema junto com a tela. A casca de sempre não passa os sub-sistemas
 * (`subs`): nada muda nela. O botão fixo da barra superior liga e desliga a divisão (`alternar`).
 */

export type Painel = 'principal' | 'lado';
export const PARAM_LADO = 'lado';
/** Sub-sistema do painel ao lado (casca do novo desenho). */
export const PARAM_SUB_LADO = 'lado_sub';
export const ID_LADO = 'conteudo-lado';
/** Largura mínima de cada painel lado a lado. */
export const LARGURA_MIN_PAINEL = 360;
/** Abaixo de 1024 px (onde o menu lateral vira gaveta), os painéis viram abas. */
export const CONSULTA_ABAS = '(max-width: 1023px)';
/** Teto do caminho guardado na URL. */
export const TETO_CAMINHO_LADO = 2000;
const BASE = 'http://malgoos.invalido';

/** Código do sub-sistema do painel ao lado lido da URL (só letras minúsculas, números, `-` e `_`; o resto é ignorado). */
export function subDoLado(busca: string | URLSearchParams): string | null {
  const v = (typeof busca === 'string' ? new URLSearchParams(busca) : busca).get(PARAM_SUB_LADO);
  return v && /^[a-z0-9_-]{1,40}$/.test(v) ? v : null;
}

/**
 * Caminho do painel ao lado lido da URL da janela: só caminho interno (uma barra no começo, sem esquema nem host), até o teto,
 * sem o próprio `lado` dentro; o resto é ignorado (null).
 */
export function caminhoDoLado(busca: string | URLSearchParams): string | null {
  const v = (typeof busca === 'string' ? new URLSearchParams(busca) : busca).get(PARAM_LADO);
  return normalizarCaminho(v);
}

export function normalizarCaminho(v: string | null | undefined): string | null {
  if (!v || v.length > TETO_CAMINHO_LADO || !v.startsWith('/') || v.startsWith('//') || v.includes('\\') || [...v].some((c) => c.charCodeAt(0) < 32)) return null;
  let u: URL;
  try {
    u = new URL(v, BASE);
  } catch {
    return null;
  }
  if (u.origin !== BASE) return null;
  u.searchParams.delete(PARAM_LADO);
  u.searchParams.delete(PARAM_SUB_LADO);
  return `${u.pathname}${u.search}${u.hash}`;
}

/**
 * A busca com o painel ao lado gravado (ou tirado, com null), sem mexer nos outros parâmetros. O sub-sistema do lado vai junto
 * quando dado; sem o `lado`, sai também.
 */
export function buscaComLado(busca: string, lado: string | null, sub?: string | null): string {
  const p = new URLSearchParams(busca);
  if (lado) p.set(PARAM_LADO, lado);
  else p.delete(PARAM_LADO);
  if (lado && sub) p.set(PARAM_SUB_LADO, sub);
  else if (!lado || sub === null) p.delete(PARAM_SUB_LADO);
  const q = p.toString();
  return q ? `?${q}` : '';
}

/** Limites da proporção do painel 1 (em %) para a largura da área: 360 px por painel; sem medida, 20 a 80. */
export function limitesDaProporcao(largura: number): [number, number] {
  const min = largura > 0 ? Math.min(50, (LARGURA_MIN_PAINEL / largura) * 100) : 20;
  return [min, 100 - min];
}

export function limitarProporcao(p: number, largura: number): number {
  const [min, max] = limitesDaProporcao(largura);
  const n = Number.isFinite(p) ? p : 50;
  return Math.round(Math.min(max, Math.max(min, n)) * 10) / 10;
}

/** Proporção depois da tecla na divisória (setas: 5%, com Shift 1%; Home e End: os limites), ou null se a tecla não mexe. */
export function proporcaoDaTecla(p: number, tecla: string, largura: number, fino = false): number | null {
  const passo = fino ? 1 : 5;
  const [min, max] = limitesDaProporcao(largura);
  if (tecla === 'ArrowLeft') return limitarProporcao(p - passo, largura);
  if (tecla === 'ArrowRight') return limitarProporcao(p + passo, largura);
  if (tecla === 'Home') return limitarProporcao(min, largura);
  if (tecla === 'End') return limitarProporcao(max, largura);
  return null;
}

export const chaveProporcao = (usuarioId: string) => `malgoos.divisao.proporcao.${usuarioId}`;

export function lerProporcao(usuarioId: string): number {
  try {
    const v = Number(localStorage.getItem(chaveProporcao(usuarioId)));
    return v > 0 && v < 100 ? v : 50;
  } catch {
    return 50;
  }
}

export function gravarProporcao(usuarioId: string, p: number) {
  try {
    localStorage.setItem(chaveProporcao(usuarioId), String(p));
  } catch {
    /* armazenamento bloqueado: a proporção vale até recarregar */
  }
}

function useConsulta(consulta: string): boolean {
  const [sim, setSim] = useState(() => typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia(consulta).matches);
  useEffect(() => {
    if (!window.matchMedia) return;
    const m = window.matchMedia(consulta);
    const mudou = () => setSim(m.matches);
    mudou();
    m.addEventListener?.('change', mudou);
    return () => m.removeEventListener?.('change', mudou);
  }, [consulta]);
  return sim;
}

export interface TelaDividida {
  /** Há rotas para o painel ao lado (o Shell montado pelo App). */
  disponivel: boolean;
  ativo: boolean;
  /** Abaixo de 1024 px: os painéis são abas. */
  emAbas: boolean;
  foco: Painel;
  caminhoLado: string | null;
  /** Caminho do painel principal, sem o `lado`. */
  caminhoPrincipal: string;
  proporcao: number;
  definirProporcao: (p: number, gravar?: boolean) => void;
  definirFoco: (p: Painel) => void;
  /** Muda o painel ativo e, com `mover`, leva o foco do teclado para ele. */
  focar: (p: Painel, mover?: boolean) => void;
  /** Abre o caminho no outro painel (o que não é `origem`); sem tela dividida, abre o painel ao lado. */
  abrirAoLado: (para: string, origem: Painel) => void;
  navegarNoLado: (para: string) => void;
  fechar: () => void;
  trocar: () => void;
  /** Botão fixo da barra superior: sem divisão, abre a tela atual ao lado; com ela, fecha o painel 2. */
  alternar: () => void;
  /** Casca do novo desenho: o sub-sistema do painel 2 (null fora dela ou sem divisão). */
  subLado: string | null;
  definirSubLado: (sub: string) => void;
}

/** Sub-sistema do painel 1 (o da aba), passado pela casca do novo desenho. */
export interface SubsDaDivisao {
  principal: string | null;
  escolherPrincipal: (sub: string) => void;
}

const CtxDivisao = createContext<TelaDividida | null>(null);
const CtxPainel = createContext<Painel>('principal');
export const useTelaDividida = () => useContext(CtxDivisao);
/** Em qual painel o componente está (fora da tela dividida, o principal). */
export const usePainel = () => useContext(CtxPainel);
export const ProvedorTelaDividida = CtxDivisao.Provider;

const elementoDoPainel = (p: Painel) => document.getElementById(p === 'lado' ? ID_LADO : 'conteudo');

interface Interno {
  geracao: number;
  inicialLado: string;
  registrarNavegacao: (n: NavigateFunction | null) => void;
  aoMudarLado: (caminho: string, pathname: string) => void;
}

/** Estado da tela dividida, dono do Shell (fica dentro do roteador da janela). */
export function useEstadoTelaDividida(usuarioId: string, disponivel: boolean, subs?: SubsDaDivisao): TelaDividida & { interno: Interno } {
  const local = useLocation();
  const navegar = useNavigate();
  const [lado, setLado] = useState<string | null>(() => (disponivel ? caminhoDoLado(local.search) : null));
  const comSubs = !!subs;
  const [subLado, setSubLado] = useState<string | null>(() => (disponivel && subs && caminhoDoLado(local.search) ? (subDoLado(local.search) ?? subs.principal) : null));
  const subPrincipal = subs?.principal ?? null;
  const escolherPrincipal = subs?.escolherPrincipal;
  const [geracao, setGeracao] = useState(0);
  const [foco, setFoco] = useState<Painel>(() => 'principal');
  const [pedidoDeFoco, setPedidoDeFoco] = useState<{ p: Painel; n: number } | null>(null);
  const [proporcao, setProporcao] = useState(() => lerProporcao(usuarioId));
  const navegarLado = useRef<NavigateFunction | null>(null);
  const emAbas = useConsulta(CONSULTA_ABAS);
  const ativo = disponivel && lado !== null;
  const caminhoPrincipal = `${local.pathname}${buscaComLado(local.search, null)}${local.hash}`;

  // A URL da janela sempre leva o `lado` do estado: a navegação do painel principal (link, Navigate, voltar) não o carrega, e
  // o parâmetro volta por substituição (sem entrada nova no histórico).
  const subNaUrl = comSubs && lado ? subLado : null;
  useEffect(() => {
    if (!disponivel) return;
    const atual = new URLSearchParams(local.search);
    if (atual.get(PARAM_LADO) === lado && atual.get(PARAM_SUB_LADO) === subNaUrl) return;
    // Navegação em curso (o roteador da janela entrega a rota nova numa transição, depois do estado do `lado`): o histórico
    // já está na entrada nova e substituí-la pela antiga desfaria a navegação. O efeito volta quando a rota chegar.
    const doRoteador = new URL(`${local.pathname}${local.search}`, window.location.href);
    if (`${window.location.pathname}${window.location.search}` !== `${doRoteador.pathname}${doRoteador.search}`) return;
    navegar({ pathname: local.pathname, search: buscaComLado(local.search, lado, subNaUrl), hash: local.hash }, { replace: true, state: local.state });
  }, [disponivel, lado, subNaUrl, local, navegar]);

  useEffect(() => {
    if (pedidoDeFoco) elementoDoPainel(pedidoDeFoco.p)?.focus();
  }, [pedidoDeFoco]);

  const focar = useCallback((p: Painel, mover = true) => {
    setFoco(p);
    if (mover) setPedidoDeFoco((x) => ({ p, n: (x?.n ?? 0) + 1 }));
  }, []);

  const abrirAoLado = useCallback(
    (para: string, origem: Painel) => {
      const destino = normalizarCaminho(para);
      if (!destino) return;
      // Novo desenho: a tela vai para o outro painel com o sub-sistema de onde saiu.
      if (!ativo) {
        setLado(destino);
        if (comSubs) setSubLado(subPrincipal);
        setGeracao((g) => g + 1);
        focar('lado');
      } else if (origem === 'principal') {
        navegarLado.current?.(destino);
        if (comSubs) setSubLado(subPrincipal);
        focar('lado');
      } else {
        navegar(destino);
        if (subLado) escolherPrincipal?.(subLado);
        focar('principal');
      }
    },
    [ativo, focar, navegar, comSubs, subPrincipal, subLado, escolherPrincipal],
  );

  const fechar = useCallback(() => {
    if (!ativo) return;
    if (foco === 'principal' && lado) {
      navegar(lado);
      // Fechando o painel 1, a janela fica com a tela do 2 e o sub-sistema dele.
      if (subLado) escolherPrincipal?.(subLado);
    }
    setLado(null);
    setSubLado(null);
    focar('principal');
  }, [ativo, foco, lado, navegar, focar, subLado, escolherPrincipal]);

  const trocar = useCallback(() => {
    if (!ativo || !lado) return;
    const antes = caminhoPrincipal;
    setLado(antes);
    navegarLado.current?.(antes);
    if (comSubs) {
      setSubLado(subPrincipal);
      if (subLado) escolherPrincipal?.(subLado);
    }
    const u = new URL(lado, BASE);
    navegar({ pathname: u.pathname, search: buscaComLado(u.search, antes, comSubs ? subPrincipal : undefined), hash: u.hash });
  }, [ativo, lado, caminhoPrincipal, navegar, comSubs, subPrincipal, subLado, escolherPrincipal]);

  const alternar = useCallback(() => {
    if (!disponivel) return;
    if (!ativo) return abrirAoLado(caminhoPrincipal, 'principal');
    setLado(null);
    setSubLado(null);
    focar('principal');
  }, [disponivel, ativo, abrirAoLado, caminhoPrincipal, focar]);

  // Atalhos: Ctrl+Alt+seta para a direita abre ao lado (ou vai ao painel 2); para a esquerda, volta ao painel 1. Com camada
  // aberta (Modal, Drawer, tour), nada: o foco é da camada.
  useEffect(() => {
    if (!disponivel) return;
    const h = (e: globalThis.KeyboardEvent) => {
      if (!e.ctrlKey || !e.altKey || e.metaKey || e.shiftKey || haCamadaAberta()) return;
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        if (ativo) focar('lado');
        else abrirAoLado(caminhoPrincipal, 'principal');
      } else if (e.key === 'ArrowLeft' && ativo) {
        e.preventDefault();
        focar('principal');
      }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [disponivel, ativo, focar, abrirAoLado, caminhoPrincipal]);

  const definirProporcao = useCallback(
    (p: number, gravar = true) => {
      setProporcao(p);
      if (gravar) gravarProporcao(usuarioId, p);
    },
    [usuarioId],
  );

  const interno = useMemo<Interno>(
    () => ({
      geracao,
      inicialLado: lado ?? '/',
      registrarNavegacao: (n) => void (navegarLado.current = n),
      aoMudarLado: (caminho, pathname) => {
        definirCaminhoDoLado(pathname);
        setLado((x) => (x === null ? x : (normalizarCaminho(caminho) ?? x)));
      },
    }),
    // A rota inicial só conta na montagem do roteador do painel (`geracao`).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [geracao],
  );

  return {
    disponivel,
    ativo,
    emAbas,
    foco: ativo ? foco : 'principal',
    caminhoLado: ativo ? lado : null,
    caminhoPrincipal,
    proporcao,
    definirProporcao,
    definirFoco: setFoco,
    focar,
    abrirAoLado,
    navegarNoLado: (para) => {
      const destino = normalizarCaminho(para);
      if (destino) navegarLado.current?.(destino);
    },
    fechar,
    trocar,
    alternar,
    subLado: ativo && comSubs ? subLado : null,
    definirSubLado: setSubLado,
    interno,
  };
}

/** O caminho (com busca e hash) do link, para levar ao outro roteador. */
function caminhoDoLink(a: HTMLAnchorElement): string | null {
  const href = a.getAttribute('href');
  if (!href) return null;
  try {
    const u = new URL(href, BASE);
    return u.origin === BASE ? `${u.pathname}${u.search}${u.hash}` : null;
  } catch {
    return null;
  }
}

/**
 * Menu lateral com a tela dividida: o clique num item vai para o painel ativo (o painel 2 navega no roteador dele; Ctrl,
 * Shift, Alt e o botão do meio seguem o navegador), o item ativo é o do painel ativo e o menu de contexto do item tem "Abrir
 * ao lado". Sem tela dividida, nada muda.
 */
export function useMenuDaDivisao(pathnameDaJanela: string, aoEscolher?: () => void) {
  const d = useTelaDividida();
  const { t } = useTranslation();
  const celular = useCelular();
  const [contexto, setContexto] = useState<{ para: string; rotulo: string; x: number; y: number; gatilho: HTMLElement } | null>(null);
  const pathname = d?.ativo && d.foco === 'lado' && d.caminhoLado ? new URL(d.caminhoLado, BASE).pathname : pathnameDaJanela;
  const aoClicar = (e: MouseEvent<HTMLElement>) => {
    if (!d?.ativo || d.foco !== 'lado' || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    const a = (e.target as Element).closest<HTMLAnchorElement>('a[href]');
    if (!a || a.dataset.itemMenu === 'subsistemas') return;
    const para = caminhoDoLink(a);
    if (!para) return;
    // Na captura: o Link do roteador da janela vê o `defaultPrevented` e não navega o painel 1.
    e.preventDefault();
    d.navegarNoLado(para);
  };
  const aoContexto = (e: MouseEvent<HTMLElement>) => {
    if (!d?.disponivel) return;
    const a = (e.target as Element).closest<HTMLAnchorElement>('a[data-item-menu]');
    if (!a || a.dataset.itemMenu === 'subsistemas') return;
    const para = caminhoDoLink(a);
    if (!para) return;
    e.preventDefault();
    const r = a.getBoundingClientRect();
    const rotulo = (a.getAttribute('aria-label') ?? a.textContent ?? '').trim();
    setContexto({ para, rotulo, x: r.left + 8, y: r.bottom, gatilho: a });
  };
  const rotuloAbrir = celular ? t('divisao.abrirEmAba') : t('divisao.abrirAoLado');
  const menu = contexto && d ? (
    <MenuDeContexto
      rotulo={t('divisao.opcoesDoItem', { item: contexto.rotulo })}
      x={contexto.x}
      y={contexto.y}
      gatilho={contexto.gatilho}
      aoFechar={() => setContexto(null)}
      itens={[{ chave: 'abrir-ao-lado', rotulo: rotuloAbrir, icone: <Columns2 className="size-4 shrink-0" aria-hidden />, aoClicar: () => {
            aoEscolher?.();
            d.abrirAoLado(contexto.para, d.ativo ? d.foco : 'principal');
          } }]}
    />
  ) : null;
  return { pathname, aoClicar, aoContexto, menu };
}

/**
 * Menu de contexto (botão direito, toque longo, tecla de menu ou Shift+F10 no item): o mesmo visual e o mesmo teclado do
 * `Menu` do núcleo (setas, Esc devolve o foco ao item, Tab e clique fora fecham), na posição do item, dentro da janela.
 */
function MenuDeContexto({ rotulo, x, y, gatilho, itens, aoFechar }: { rotulo: string; x: number; y: number; gatilho: HTMLElement; itens: { chave: string; rotulo: string; icone?: ReactNode; aoClicar: () => void }[]; aoFechar: () => void }) {
  const painel = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const p = painel.current;
    if (!p) return;
    const r = p.getBoundingClientRect();
    p.style.left = `${Math.max(8, Math.min(x, window.innerWidth - r.width - 8))}px`;
    p.style.top = `${Math.max(8, Math.min(y + 4, window.innerHeight - r.height - 8))}px`;
    p.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
  }, [x, y]);
  useEffect(() => {
    const fora = (e: globalThis.MouseEvent) => {
      if (!painel.current?.contains(e.target as Node)) aoFechar();
    };
    document.addEventListener('mousedown', fora);
    return () => document.removeEventListener('mousedown', fora);
  }, [aoFechar]);
  const fechar = (devolver: boolean) => {
    aoFechar();
    if (devolver && gatilho.isConnected) gatilho.focus();
  };
  const aoTeclar = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      return fechar(true);
    }
    if (e.key === 'Tab') return fechar(false);
    const lista = [...(painel.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? [])];
    const i = lista.indexOf(document.activeElement as HTMLElement);
    const vai = e.key === 'ArrowDown' ? (i + 1) % lista.length : e.key === 'ArrowUp' ? (i - 1 + lista.length) % lista.length : e.key === 'Home' ? 0 : e.key === 'End' ? lista.length - 1 : -1;
    if (vai < 0) return;
    e.preventDefault();
    lista[vai]?.focus();
  };
  return createPortal(
    <div
      ref={painel}
      role="menu"
      aria-label={rotulo}
      data-menu-contexto-lateral=""
      onKeyDown={aoTeclar}
      onBlur={(e) => {
        const para = e.relatedTarget as Node | null;
        if (para && !painel.current?.contains(para)) aoFechar();
      }}
      className="fixed z-50 flex w-60 flex-col gap-0.5 rounded-lg border border-borda bg-sup-2 p-1.5 shadow-xl"
    >
      {itens.map((i) => (
        <button
          key={i.chave}
          type="button"
          role="menuitem"
          tabIndex={-1}
          title={i.rotulo}
          data-item-contexto={i.chave}
          onClick={() => {
            fechar(false);
            i.aoClicar();
          }}
          className="flex min-h-10 min-w-0 items-center gap-2 rounded px-2 text-left text-sm text-tinta-1 hover:bg-sup-3 focus:bg-sup-3"
        >
          {i.icone}
          <span className="min-w-0 truncate" title={i.rotulo}>
            {i.rotulo}
          </span>
        </button>
      ))}
    </div>,
    document.body,
  );
}

/** "Abrir ao lado" no topo da página (ícone de colunas): abre esta tela no outro painel. No celular, "em outra aba do painel". */
export function BotaoAbrirAoLado() {
  const d = useTelaDividida();
  const painel = usePainel();
  const local = useLocation();
  const celular = useCelular();
  const { t } = useTranslation();
  if (!d?.disponivel) return null;
  const rotulo = celular ? t('divisao.abrirEmAba') : t('divisao.abrirAoLado');
  return (
    <Botao
      variante="fantasma"
      tamanho="sm"
      data-abrir-ao-lado=""
      aria-label={rotulo}
      title={celular ? rotulo : t('divisao.tituloAbrir')}
      icone={<Columns2 className="size-4" aria-hidden />}
      onClick={() => d.abrirAoLado(`${local.pathname}${buscaComLado(local.search, null)}${local.hash}`, painel)}
    />
  );
}

/** Avisa o Shell da rota do painel ao lado e entrega a navegação dele (o menu lateral e "Trocar lados" usam). */
function OuvinteDoLado({ interno }: { interno: Interno }) {
  const local = useLocation();
  const navegar = useNavigate();
  useEffect(() => {
    interno.registrarNavegacao(navegar);
    return () => interno.registrarNavegacao(null);
  }, [interno, navegar]);
  useEffect(() => {
    interno.aoMudarLado(`${local.pathname}${local.search}${local.hash}`, local.pathname);
  }, [interno, local.pathname, local.search, local.hash]);
  return null;
}

function ConteudoDoLado({ rotas }: { rotas: () => ReactNode }) {
  const { pathname } = useLocation();
  return <LimiteErro key={pathname.split('/').slice(0, 3).join('/')}>{<Routes>{rotas()}</Routes>}</LimiteErro>;
}

const SEM_ROTA = { outlet: null, matches: [], isDataRoute: false };

/**
 * O painel ao lado: um roteador em memória isolado do da janela (o `MemoryRouter` não aceita outro roteador acima; os
 * contextos de localização e de rota são zerados aqui), com o próprio lugar do botão primário.
 */
function PainelDoLado({ interno, rotas, rotulo, ativo }: { interno: Interno; rotas: () => ReactNode; rotulo: string; ativo: boolean }) {
  return (
    <UNSAFE_LocationContext.Provider value={null as never}>
      <UNSAFE_RouteContext.Provider value={SEM_ROTA}>
        <MemoryRouter key={interno.geracao} initialEntries={[interno.inicialLado]}>
          <OuvinteDoLado interno={interno} />
          <ProvedorAcaoPrimaria>
            <section
              id={ID_LADO}
              tabIndex={-1}
              aria-label={rotulo}
              data-painel-conteudo="lado"
              data-painel-ativo={ativo ? '' : undefined}
              className="mx-auto w-full max-w-7xl min-w-0 flex-1 px-4 py-6 focus:outline-none sm:px-6 lg:px-8"
            >
              <ConteudoDoLado rotas={rotas} />
            </section>
          </ProvedorAcaoPrimaria>
        </MemoryRouter>
      </UNSAFE_RouteContext.Provider>
    </UNSAFE_LocationContext.Provider>
  );
}

function useLarguraDe(ref: RefObject<HTMLElement | null>, ligado: boolean) {
  const [largura, setLargura] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!ligado || !el) return;
    const medir = () => setLargura(el.getBoundingClientRect().width);
    medir();
    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(medir);
      ro.observe(el);
      return () => ro.disconnect();
    }
    window.addEventListener('resize', medir);
    return () => window.removeEventListener('resize', medir);
  }, [ref, ligado]);
  return largura;
}

/** Divisória entre os painéis: arrastar (mouse e toque), setas (Shift: passo fino), Home e End; duplo clique volta a 50/50. */
function Divisoria({ d, largura, area }: { d: TelaDividida; largura: number; area: RefObject<HTMLDivElement | null> }) {
  const { t } = useTranslation();
  const arrastando = useRef(false);
  const p = limitarProporcao(d.proporcao, largura);
  const [min, max] = limitesDaProporcao(largura);
  const pelaPosicao = (x: number) => {
    const r = area.current?.getBoundingClientRect();
    if (!r || r.width <= 0) return null;
    return limitarProporcao(((x - r.left) / r.width) * 100, r.width);
  };
  return (
    <div
      role="separator"
      tabIndex={0}
      aria-orientation="vertical"
      aria-label={t('divisao.divisoria')}
      aria-controls={`conteudo ${ID_LADO}`}
      aria-valuenow={Math.round(p)}
      aria-valuemin={Math.round(min)}
      aria-valuemax={Math.round(max)}
      aria-valuetext={t('divisao.proporcao', { p: Math.round(p) })}
      data-divisoria=""
      onPointerDown={(e: PointerEvent<HTMLDivElement>) => {
        if (e.button !== 0) return;
        e.preventDefault();
        arrastando.current = true;
        e.currentTarget.setPointerCapture?.(e.pointerId);
      }}
      onPointerMove={(e) => {
        if (!arrastando.current) return;
        const n = pelaPosicao(e.clientX);
        if (n !== null) d.definirProporcao(n, false);
      }}
      onPointerUp={(e) => {
        if (!arrastando.current) return;
        arrastando.current = false;
        e.currentTarget.releasePointerCapture?.(e.pointerId);
        const n = pelaPosicao(e.clientX);
        d.definirProporcao(n ?? p);
      }}
      onPointerCancel={() => {
        arrastando.current = false;
      }}
      onKeyDown={(e) => {
        const n = proporcaoDaTecla(p, e.key, largura, e.shiftKey);
        if (n === null) return;
        e.preventDefault();
        d.definirProporcao(n);
      }}
      onDoubleClick={() => d.definirProporcao(50)}
      className="w-2 shrink-0 cursor-col-resize touch-none bg-borda hover:bg-foco focus-visible:bg-foco"
    />
  );
}

/** Linha de cima da tela dividida: as abas "Painel 1 | Painel 2" (abaixo de 1024 px), "Trocar lados" e "Fechar o painel N". */
function BarraDaDivisao({ d }: { d: TelaDividida }) {
  const { t } = useTranslation();
  const n = d.foco === 'lado' ? 2 : 1;
  return (
    <div data-barra-divisao="" data-sem-impressao role="group" aria-label={t('divisao.barra')} className="flex min-w-0 flex-wrap items-center gap-2 border-b border-borda px-4 py-1 sm:px-6 lg:px-8">
      {d.emAbas && (
        <Abas<Painel>
          idBase="divisao"
          grupo="divisao"
          rotulo={t('divisao.barra')}
          ativa={d.foco}
          aoMudar={(p) => d.focar(p, false)}
          itens={[
            { chave: 'principal', rotulo: t('divisao.painel', { n: 1 }) },
            { chave: 'lado', rotulo: t('divisao.painel', { n: 2 }) },
          ]}
        />
      )}
      <div className="ml-auto flex items-center gap-1">
        <Botao variante="fantasma" tamanho="sm" data-trocar-lados="" aria-label={t('divisao.trocar')} title={t('divisao.trocar')} icone={<ArrowLeftRight className="size-4" aria-hidden />} onClick={d.trocar} />
        <Botao variante="fantasma" tamanho="sm" data-fechar-painel="" aria-label={t('divisao.fechar', { n })} title={t('divisao.fechar', { n })} icone={<X className="size-4" aria-hidden />} onClick={d.fechar} />
      </div>
    </div>
  );
}

/**
 * A área de conteúdo do Shell: o painel principal (o `<main id="conteudo">`, que vem pronto do Shell) e, com a tela dividida,
 * a divisória e o painel ao lado. A árvore do painel principal é a mesma com e sem divisão (abrir, fechar e trocar a largura
 * não remontam a tela aberta).
 */
export function AreaDoConteudo({ d, rotas, envolverLado, children }: { d: TelaDividida & { interno: Interno }; rotas?: () => ReactNode; envolverLado?: (n: ReactNode) => ReactNode; children: ReactNode }) {
  const { t } = useTranslation();
  const area = useRef<HTMLDivElement>(null);
  const comLado = d.ativo && !!rotas;
  const ladoALado = comLado && !d.emAbas;
  const largura = useLarguraDe(area, ladoALado);
  const p = limitarProporcao(d.proporcao, largura);
  const props = (painel: Painel) => {
    const ativo = d.foco === painel;
    return {
      'data-painel': painel,
      onFocusCapture: () => comLado && d.definirFoco(painel),
      onPointerDownCapture: () => comLado && d.definirFoco(painel),
      ...(comLado && d.emAbas ? { role: 'tabpanel', id: idPainel('divisao', painel), 'aria-labelledby': idAba('divisao', painel) } : {}),
      className: `flex min-w-0 flex-col ${ladoALado ? `min-h-0 overflow-y-auto border-t-2 ${ativo ? 'border-foco' : 'border-transparent'}` : 'flex-1'} ${comLado && d.emAbas && !ativo ? 'hidden' : ''}`,
      style: ladoALado ? { flexGrow: painel === 'principal' ? p : 100 - p, flexBasis: 0 } : undefined,
    };
  };
  return (
    <CtxPainel.Provider value="principal">
      <div data-area-conteudo={comLado ? (d.emAbas ? 'abas' : 'lado-a-lado') : undefined} className={`flex min-w-0 flex-1 flex-col ${ladoALado ? 'min-h-0' : ''}`}>
        {comLado && <BarraDaDivisao d={d} />}
        <div ref={area} className={`flex min-w-0 flex-1 ${ladoALado ? 'min-h-0 flex-row' : 'flex-col'}`}>
          <div {...props('principal')}>{children}</div>
          {ladoALado && <Divisoria d={d} largura={largura} area={area} />}
          {comLado && rotas && (
            <div {...props('lado')}>
              <CtxPainel.Provider value="lado">
                {/* Novo desenho: o painel 2 com o sub-sistema dele (a casca passa o provedor). */}
                {(envolverLado ?? ((n: ReactNode) => n))(<PainelDoLado interno={d.interno} rotas={rotas} rotulo={t('divisao.painel', { n: 2 })} ativo={d.foco === 'lado'} />)}
              </CtxPainel.Provider>
            </div>
          )}
        </div>
      </div>
    </CtxPainel.Provider>
  );
}
